import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PageTitleService {
  private router = inject(Router);
  private title = inject(Title);
  private destroyRef = inject(DestroyRef);

  private readonly baseTitle = 'Wall St. Yardie';
  private initialized = false;
  private routeOverrides = new Map<string, string>();

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.updateTitle(this.router.url);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        this.updateTitle(event.urlAfterRedirects);
      });
  }

  setRouteTitleOverride(title: string | null): void {
    const normalizedUrl = this.normalizeUrl(this.router.url);
    const normalizedTitle = title?.trim();

    if (normalizedTitle) {
      this.routeOverrides.set(normalizedUrl, normalizedTitle);
    } else {
      this.routeOverrides.delete(normalizedUrl);
    }

    this.updateTitle(this.router.url);
  }

  private updateTitle(url: string): void {
    const pageTitle = this.buildTitle(url);
    this.title.setTitle(pageTitle);
  }

  private buildTitle(url: string): string {
    const normalizedUrl = this.normalizeUrl(url);
    const segments = normalizedUrl.split('/').filter(Boolean);

    if (segments.length === 0 || segments[0] === 'resolving') {
      return this.baseTitle;
    }

    const firstSegment = segments[0].toLowerCase();
    const dynamicTitle = this.getDynamicTitle(firstSegment, segments, normalizedUrl);
    if (dynamicTitle) {
      return this.formatTitle(dynamicTitle);
    }

    const staticTitle = this.getStaticTitle(firstSegment, segments[1]);
    return staticTitle ? this.formatTitle(staticTitle) : this.baseTitle;
  }

  private getDynamicTitle(firstSegment: string, segments: string[], normalizedUrl: string): string | null {
    if (firstSegment === 'stocks' && segments[1]) {
      return segments[1].toUpperCase();
    }

    if (firstSegment === 'watchlist' && segments[1]) {
      return this.routeOverrides.get(normalizedUrl) ?? 'Watchlist';
    }

    if (firstSegment === 'sectors' && segments[1]) {
      return this.formatRouteName(segments[1]);
    }

    if (firstSegment === 'industries' && segments[1]) {
      return this.formatRouteName(segments[1]);
    }

    if (firstSegment === 'picks' && segments[1]) {
      return this.formatRouteName(segments[1]);
    }

    return null;
  }

  private getStaticTitle(firstSegment: string, secondSegment?: string): string | null {
    const topLevelTitles: Record<string, string> = {
      dashboard: 'Dashboard',
      sectors: 'Sectors',
      congress: 'Congress',
      watchlist: 'Watchlist',
      screen: 'Screener',
      picks: 'Picks',
      profile: 'Profile',
      pricing: 'Pricing',
      tools: 'Tools',
      terms: 'Terms',
      privacy: 'Privacy',
      disclaimer: 'Disclaimer',
      cookies: 'Cookies',
      refund: 'Refund Policy',
      'do-not-sell': 'Do Not Sell',
      'complete-profile': 'Complete Profile',
      'accept-terms': 'Accept Terms',
      error: 'Error',
    };

    if (firstSegment === 'tools' && secondSegment) {
      const toolTitles: Record<string, string> = {
        dcf: 'Bad Man Forward Calculator',
        'graham-price': 'Graham Price Calculator',
        'growth-rate': 'Growth Rate Calculator',
        'money-a-run': 'Money A Run Calculator',
        'big-up-yield': 'Big Up Yield Calculator',
      };
      return toolTitles[secondSegment] ?? topLevelTitles[firstSegment];
    }

    if (firstSegment === 'profile' && secondSegment) {
      const profileTitles: Record<string, string> = {
        'data-export': 'Data Export',
        'delete-account': 'Delete Account',
        'update-subscription': 'Update Subscription',
      };
      return profileTitles[secondSegment] ?? topLevelTitles[firstSegment];
    }

    if (firstSegment === 'sign-up' && secondSegment === 'verify-email') {
      return 'Verify Email';
    }

    return topLevelTitles[firstSegment] ?? null;
  }

  private normalizeUrl(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  private formatRouteName(value: string): string {
    const normalized = value
      .replace(/~/g, ' ')
      .replace(/-/g, ' ')
      .trim();

    return normalized
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private formatTitle(suffix: string): string {
    return suffix ? `${this.baseTitle} - ${suffix}` : this.baseTitle;
  }
}
